import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { member } from '../lib/api';
import type { EventRsvp, Notification, Player, TeamEvent, VideoClip } from '../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Heading, Subheading } from '../ui/heading';
import { Text } from '../ui/text';
import { EmptyState, Panel, StatCard } from '../components/PageChrome';

interface PlayerBundle {
  teamId: string;
  player: Player;
  clips: VideoClip[];
}

export default function MemberHome() {
  const [teams, setTeams] = useState<Array<{ member: { teamId: string; roles: string[]; linkedPlayerIds: string[] }; team?: { teamId: string; name: string } }>>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [players, setPlayers] = useState<PlayerBundle[]>([]);
  const [rsvpSaved, setRsvpSaved] = useState<Record<string, EventRsvp['status']>>({});
  const [error, setError] = useState<string>();

  useEffect(() => {
    void (async () => {
      try {
        const [t, n] = await Promise.all([member.myTeams(), member.notifications()]);
        setTeams(t.teams);
        setNotifications(n.notifications);
        const eventSets = await Promise.all(
          t.teams.map((row) => member.events(row.member.teamId).catch(() => ({ events: [] as TeamEvent[] }))),
        );
        setEvents(eventSets.flatMap((set) => set.events).sort((a, b) => a.startAt.localeCompare(b.startAt)));
        const playerRows = await Promise.all(
          t.teams.flatMap((row) =>
            row.member.linkedPlayerIds.map(async (playerId) => {
              const [player, clips] = await Promise.all([
                member.player(row.member.teamId, playerId),
                member.playerClips(row.member.teamId, playerId).catch(() => ({ clips: [] as VideoClip[] })),
              ]);
              return { teamId: row.member.teamId, player, clips: clips.clips };
            }),
          ),
        );
        setPlayers(playerRows);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  const unread = notifications.filter((n) => !n.readAt);
  const upcoming = useMemo(() => events.filter((e) => e.status === 'SCHEDULED').slice(0, 8), [events]);
  const linkedPlayerForTeam = (teamId: string) =>
    teams.find((row) => row.member.teamId === teamId)?.member.linkedPlayerIds[0];

  const setAvailability = async (event: TeamEvent, status: EventRsvp['status']) => {
    try {
      const playerId = linkedPlayerForTeam(event.teamId);
      await member.rsvp(event.teamId, event.eventId, { status, playerId });
      setRsvpSaved((cur) => ({ ...cur, [event.eventId]: status }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const markRead = async (notificationId: string) => {
    await member.markNotificationRead(notificationId);
    setNotifications((cur) =>
      cur.map((n) =>
        n.notificationId === notificationId ? { ...n, readAt: new Date().toISOString() } : n,
      ),
    );
  };

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading>My BaseballSTRM</Heading>
          <Text className="mt-1">Your teams, upcoming events, RSVPs, notifications, player profiles, and clips.</Text>
        </div>
        <Button href="/me/profile" outline>
          Profile
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Teams" value={teams.length} tone="emerald" />
        <StatCard label="Upcoming" value={upcoming.length} />
        <StatCard label="Unread" value={unread.length} tone="amber" />
      </div>

      {error ? <Text className="text-red-400">{error}</Text> : null}

      <section className="space-y-3">
        <Subheading>Teams</Subheading>
        {teams.length === 0 ? (
          <EmptyState title="No approved teams yet" body="Follow or accept an invite to see your team workspace." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {teams.map((row) => (
              <Panel key={row.member.teamId}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-zinc-100">{row.team?.name ?? row.member.teamId}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {row.member.roles.map((r) => <Badge key={r} color="zinc">{r}</Badge>)}
                    </div>
                  </div>
                  <Button href={`/teams/${row.member.teamId}`} outline>Open</Button>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <Subheading>Upcoming</Subheading>
        {upcoming.length === 0 ? (
          <Panel><Text>No upcoming events.</Text></Panel>
        ) : (
          <ul className="space-y-3">
            {upcoming.map((e) => {
              const saved = rsvpSaved[e.eventId];
              return (
              <li key={e.eventId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="font-medium text-zinc-100">{e.title}</div>
                    <Text className="mt-1 text-sm">{new Date(e.startAt).toLocaleString()}{e.locationName ? ` · ${e.locationName}` : ''}</Text>
                    {saved ? <Badge className="mt-2" color="emerald">{saved.replace('_', ' ')}</Badge> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button outline onClick={() => void setAvailability(e, 'GOING')}>Going</Button>
                    <Button outline onClick={() => void setAvailability(e, 'NOT_GOING')}>Not going</Button>
                    <Button outline onClick={() => void setAvailability(e, 'MAYBE')}>Maybe</Button>
                    <Link className="text-sm text-emerald-300" to={`/teams/${e.teamId}`}>Team page</Link>
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <Subheading>Players</Subheading>
        {players.length === 0 ? (
          <Panel><Text>Linked player profiles will appear here after staff connects your account.</Text></Panel>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {players.map(({ teamId, player, clips }) => (
              <Panel key={`${teamId}-${player.playerId}`} className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-zinc-100">#{player.jerseyNumber} {player.name}</div>
                    <Text className="mt-1 text-sm">{player.primaryPositions.join(', ') || 'No positions set'}</Text>
                  </div>
                  <Badge color={clips.length > 0 ? 'emerald' : 'zinc'}>{clips.length} clips</Badge>
                </div>
                {clips.slice(0, 3).map((clip) => (
                  <div key={clip.clipId} className="rounded border border-zinc-800 bg-zinc-950 p-3 text-sm">
                    <div className="font-medium text-zinc-100">{clip.title}</div>
                    <Text className="mt-1 text-xs">{clip.playType ?? 'Play'} · {clip.startSec}s-{clip.endSec}s</Text>
                  </div>
                ))}
              </Panel>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <Subheading>Notifications</Subheading>
        {notifications.length === 0 ? (
          <Panel><Text>No notifications yet.</Text></Panel>
        ) : (
          <ul className="space-y-2">
            {notifications.slice(0, 12).map((n) => (
              <li key={n.notificationId} className="rounded border border-zinc-800 bg-zinc-900 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-zinc-100">{n.title}</div>
                    <Text className="mt-1 text-sm">{n.body}</Text>
                  </div>
                  {!n.readAt ? (
                    <Button outline onClick={() => void markRead(n.notificationId)}>Mark read</Button>
                  ) : (
                    <Badge color="zinc">Read</Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
